// Capability → available sub-examples (from capability_mapping.json)
// capabilityOverride: send this capability to the API instead of the parent group key
// versions: which API versions have examples ('v1', 'fallback', or both)
export type ExampleEntry = { label: string; description?: string; example: string; capabilityOverride?: string; versions?: string[] };

// Build the sidebar map dynamically from the capability mapping data.
// Groups capabilities whose example paths share a prefix under the owning capability
// (e.g., jsonObjectResponse with path chat/completions/json_object_response groups under
// chatCompletion which owns chat/completions).
export type CapabilityExamples = {
  examples: Record<string, ExampleEntry[]>;
  // Grouped child capability -> parent capability whose sidebar entry already lists it
  parentOf: Record<string, string>;
};

export function buildCapabilityExamples(
  mapping: Record<string, Record<string, unknown>>,
): CapabilityExamples {
  // First, collect all capabilities with their example entries and root paths
  const allCaps: { name: string; entries: ExampleEntry[]; rootPath: string }[] = [];
  for (const [capName, capData] of Object.entries(mapping)) {
    if (!capData || (capData as { status?: string }).status !== 'enabled') continue;
    const entries = extractExampleEntries(capName, capData);
    if (entries.length === 0) continue;
    // Root path derived from file paths (strip v1/ or fallback/ prefix and extension)
    const filePaths = entries.map(e => {
      const fp = (e as { _filePath?: string })._filePath || e.example;
      return fp.replace(/^(v1|fallback)\//, '').replace(/\.[^.]+$/, '');
    });
    const rootPath = filePaths.reduce((shortest, p) => p.length < shortest.length ? p : shortest, filePaths[0]);
    allCaps.push({ name: capName, entries, rootPath });
  }

  // Group: if a capability's file paths all start with another capability's rootPath, nest it
  const result: Record<string, ExampleEntry[]> = {};
  const parentOf: Record<string, string> = {};

  for (const cap of allCaps) {
    let parent: string | undefined;
    let parentPathLen = 0;
    for (const other of allCaps) {
      if (other.name !== cap.name && cap.rootPath.startsWith(other.rootPath + '/')) {
        // Longest prefix match — avoid matching 'chat' when 'chat/completions' is more specific
        if (other.rootPath.length > parentPathLen) {
          parent = other.name;
          parentPathLen = other.rootPath.length;
        }
      }
    }
    if (parent) {
      // Add as sub-examples under the parent, with capabilityOverride
      if (!result[parent]) result[parent] = [];
      for (const entry of cap.entries) {
        result[parent].push({ ...entry, capabilityOverride: cap.name });
      }
      parentOf[cap.name] = parent;
    }
  }

  // Parent's own examples come first, then grouped children. Grouped children keep
  // their own entry so a model advertising only the child still resolves examples.
  for (const cap of allCaps) {
    const children = result[cap.name] || [];
    result[cap.name] = [...cap.entries, ...children];
  }

  return { examples: result, parentOf };
}

function extractExampleEntries(
  _capName: string,
  capData: Record<string, unknown>,
): (ExampleEntry & { _filePath: string })[] {
  const examples = new Map<string, { label: string; description?: string; versions: string[]; filePath: string }>();
  for (const ver of ['v1', 'fallback'] as const) {
    const verData = capData[ver] as { examples?: Record<string, Record<string, { description?: string; example?: string }>> } | null;
    if (!verData?.examples) continue;
    const firstLang = Object.values(verData.examples)[0];
    if (!firstLang) continue;
    for (const [exKey, exMeta] of Object.entries(firstLang)) {
      const existing = examples.get(exKey);
      if (existing) {
        if (!existing.versions.includes(ver)) existing.versions.push(ver);
      } else {
        const keyLabel = exKey.split('/').pop()!.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        examples.set(exKey, { label: keyLabel, description: exMeta?.description, versions: [ver], filePath: exMeta?.example || exKey });
      }
    }
  }
  return Array.from(examples.entries()).map(([exKey, { label, description, versions, filePath }]) => {
    const entry: ExampleEntry & { _filePath: string } = { label, description, example: exKey, _filePath: filePath };
    if (versions.length === 1) entry.versions = versions;
    return entry;
  });
}
